import { REDUCED_MARKUP_CATEGORIES } from "@loot-os/fulfillment-core/listing/types";
import { Button } from "@loot-os/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@loot-os/ui/components/dialog";
import { Label } from "@loot-os/ui/components/label";
import { useEffect, useState } from "react";

import { ListingTagsPicker } from "./ListingTagsPicker";
import { TenantsPicker } from "./TenantsPicker";

/**
 * Collects the tags and tenants a product fetch is promoted with and passes them
 * to the caller's mutation. No tenant means one shared row; each tenant picked
 * gets its own row. The caller owns `open` so the dialog can stay open while
 * the promotion runs and close on success. Both clear on every close, including
 * that one: the route component is shared across product ids, so keeping them
 * would price the next product off the last one's tags.
 *
 * While the promotion runs, no control here closes the dialog. Dismissing one
 * reads as cancelling, and nothing cancels a promotion already sent, so the
 * operator would be left believing a listing they did write never happened.
 */
export function PromoteListingDialog({
  open,
  disabled,
  isPending,
  onOpenChange,
  onPromote,
}: {
  open: boolean;
  /** Whether the row can be promoted at all; the server decides, the trigger only reflects it. */
  disabled: boolean;
  isPending: boolean;
  onOpenChange: (open: boolean) => void;
  onPromote: (input: { tags: string[]; tenantIds: string[] }) => void;
}) {
  const [tags, setTags] = useState<string[]>([]);
  const [tenantIds, setTenantIds] = useState<string[]>([]);
  // Keyed on `open` rather than the close handler, which a success-close does not go through.
  useEffect(() => {
    if (!open) {
      setTags([]);
      setTenantIds([]);
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(next) => !isPending && onOpenChange(next)}>
      <DialogTrigger
        render={<Button type="button" variant="outline" disabled={disabled || isPending} />}
      >
        Promote to listing
      </DialogTrigger>
      <DialogContent showCloseButton={!isPending}>
        <DialogHeader>
          <DialogTitle>Promote to listing</DialogTitle>
          <DialogDescription>
            Tags decide how the listing is browsed and which markup it takes: tagged only{" "}
            {[...REDUCED_MARKUP_CATEGORIES].join(", ")} it is marked up 1.15x, any other tag or mix
            1.25x. Leave tenants empty for one shared row every tenant sees.
          </DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            onPromote({ tags, tenantIds });
          }}
        >
          <div className="grid gap-2">
            <Label htmlFor="listing-tags">Tags</Label>
            <ListingTagsPicker
              id="listing-tags"
              value={tags}
              disabled={isPending}
              onChange={setTags}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="listing-tenants">Tenants</Label>
            <TenantsPicker
              id="listing-tenants"
              value={tenantIds}
              disabled={isPending}
              onChange={setTenantIds}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={isPending}
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? "Promoting…" : "Continue to promote listing"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
